import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";

export default function LateCategories() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", min_days: 0, max_days: "", color: "#F59E0B", order: 0 });

  const load = () => api.get("/late-categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) => (it.name || "").toLowerCase().includes(s));
  }, [items, q]);

  const openNew = () => { setEditing(null); setForm({ name: "", min_days: 0, max_days: "", color: "#F59E0B", order: items.length }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ name: it.name, min_days: it.min_days, max_days: it.max_days ?? "", color: it.color, order: it.order }); setOpen(true); };

  const submit = async () => {
    try {
      const payload = {
        name: form.name,
        min_days: parseInt(form.min_days),
        max_days: form.max_days === "" || form.max_days === null ? null : parseInt(form.max_days),
        color: form.color,
        order: parseInt(form.order || 0),
      };
      if (editing) await api.put(`/late-categories/${editing.id}`, payload);
      else await api.post("/late-categories", payload);
      toast.success("Tersimpan"); setOpen(false); load();
    } catch (e) { toast.error("Gagal menyimpan"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus kategori?")) return;
    await api.delete(`/late-categories/${id}`); toast.success("Terhapus"); load();
  };

  return (
    <div>
      <PageHeader title="Kategori Keterlambatan" subtitle="Atur rentang hari & warna kategori telat sesuai kebijakan showroom"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-lc-btn" className="rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-10">
                <Plus size={14} className="mr-2" /> Tambah Kategori
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-[95vw] max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "Tambah"} Kategori</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wider">Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-none" data-testid="lc-name" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Min. Hari</Label><Input type="number" value={form.min_days} onChange={(e) => setForm({ ...form, min_days: e.target.value })} className="mt-2 rounded-none font-mono-num" data-testid="lc-min" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Max. Hari (kosong = tanpa batas)</Label><Input type="number" value={form.max_days} onChange={(e) => setForm({ ...form, max_days: e.target.value })} className="mt-2 rounded-none font-mono-num" data-testid="lc-max" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Warna</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-2 rounded-none h-11 p-1" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Urutan</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-2 rounded-none font-mono-num" /></div>
              </div>
              <Button onClick={submit} data-testid="lc-submit" className="mt-4 w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11">Simpan</Button>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-4 md:p-6 lg:p-10">
        <SearchBar value={q} onChange={setQ} placeholder="Cari nama kategori..." testid="lc-search" count={filtered.length} />
        <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                <th className="p-4">Warna</th><th className="p-4">Nama</th><th className="p-4">Rentang Hari</th><th className="p-4">Urutan</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-b border-gray-100">
                  <td className="p-4"><span className="inline-block w-6 h-6" style={{ backgroundColor: it.color }} /></td>
                  <td className="p-4 font-semibold">{it.name}</td>
                  <td className="p-4 font-mono-num text-xs">{it.min_days} s/d {it.max_days === null || it.max_days === undefined ? "∞" : it.max_days} hari</td>
                  <td className="p-4 font-mono-num">{it.order}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => openEdit(it)} className="mr-3 text-gray-500 hover:text-black"><PencilSimple size={16} /></button>
                    <button onClick={() => del(it.id)} className="text-gray-500 hover:text-[#E63946]"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-sm text-gray-500">{items.length === 0 ? "Belum ada kategori" : "Tidak ada hasil"}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
